import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  X,
  Camera,
  Upload,
  AlertTriangle,
  CheckCircle2,
  User,
  Building2,
  Clock,
  FileText,
  RotateCcw,
  Loader2,
  Phone,
  MessageCircle,
  ClipboardCheck,
} from 'lucide-react';
import type { ParceiroRecepcaoItemContract, ParceiroDarEntradaResponse } from '@sstexames/shared-types';
import { clinicaService } from '../services/clinica.service';
import { formatPhone } from '../utils/formatters';
import { applyProgressiveMask } from '../utils/maskUtils';
import FormattedInput from './FormattedInput';

const isItemNoFuturo = (dateStr: string) => {
  if (!dateStr) return false;
  const agendamentoData = new Date(dateStr + 'T12:00:00');
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  agendamentoData.setHours(0, 0, 0, 0);
  return agendamentoData > hoje;
};

interface RecepcaoCheckinModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ParceiroRecepcaoItemContract | null;
  clinicaId: number;
  exigeBiometria?: boolean;
  onSuccess: (res: ParceiroDarEntradaResponse) => void;
}

export const RecepcaoCheckinModal: React.FC<RecepcaoCheckinModalProps> = ({
  isOpen,
  onClose,
  item,
  clinicaId,
  exigeBiometria = true,
  onSuccess,
}) => {
  const [biometriaFoto, setBiometriaFoto] = useState<string | null>(null);
  const [biometriaTipo, setBiometriaTipo] = useState<'WEBCAM' | 'UPLOAD'>('WEBCAM');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data local do agendamento (evita mutação direta da prop item)
  const [dataAgendamentoAtual, setDataAgendamentoAtual] = useState(() => item?.data_agendamento || '');
  const [isAjustandoData, setIsAjustandoData] = useState(false);
  const [novaData, setNovaData] = useState(new Date().toISOString().split('T')[0]);
  const [motivoAjuste, setMotivoAjuste] = useState(() =>
    item && isItemNoFuturo(item.data_agendamento) ? 'Comparecimento antecipado do colaborador' : '',
  );

  const [telefone, setTelefone] = useState(() =>
    item?.colaborador_telefone ? applyProgressiveMask('phone', item.colaborador_telefone) : '',
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  }, []);

  const handleClose = useCallback(() => {
    stopCamera();
    setBiometriaFoto(null);
    setTelefone(item?.colaborador_telefone ? applyProgressiveMask('phone', item.colaborador_telefone) : '');
    setError(null);
    setCameraError(null);
    setIsAjustandoData(false);
    setMotivoAjuste('');
    setNovaData(new Date().toISOString().split('T')[0]);
    setDataAgendamentoAtual(item?.data_agendamento || '');
    onClose();
  }, [stopCamera, onClose, item]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
        audio: false,
      });

      streamRef.current = stream;
      setIsCameraActive(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch((err) => {
            console.error('Erro ao iniciar playback de vídeo:', err);
          });
        }
      }, 100);
    } catch (err: unknown) {
      console.error('Erro ao acessar webcam:', err);
      setCameraError(
        'Não foi possível acessar a câmera. Verifique as permissões do navegador ou anexe a foto manualmente.',
      );
      setIsCameraActive(false);
    }
  };

  const captureSnapshot = () => {
    if (!videoRef.current) return;

    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Inverter horizontalmente para efeito espelho natural
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setBiometriaFoto(dataUrl);
      setBiometriaTipo('WEBCAM');
      setError(null);
      stopCamera();
    } catch (e) {
      console.error('Erro ao capturar foto da câmera:', e);
      setCameraError('Erro ao capturar imagem da câmera.');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Formato inválido. Selecione um arquivo de imagem (.jpg, .jpeg, .png).');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('O tamanho da foto não pode exceder 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setBiometriaFoto(reader.result);
        setBiometriaTipo('UPLOAD');
        setError(null);
        stopCamera();
      }
    };
    reader.readAsDataURL(file);
  };

  const dataNoFuturo = isItemNoFuturo(dataAgendamentoAtual);
  const rawTel = telefone.replace(/\D/g, '');
  const isTelefoneValido = rawTel.length === 10 || rawTel.length === 11;
  const isBiometriaValida = !exigeBiometria || !!biometriaFoto;
  const isMotivoValido = !dataNoFuturo || motivoAjuste.trim().length > 0;
  const canConfirm = !submitting && isTelefoneValido && isBiometriaValida && isMotivoValido;

  const handleConfirmar = async () => {
    if (!item) return;

    if (!isTelefoneValido) {
      setError(
        'O número de celular com DDD (10 ou 11 dígitos) é obrigatório para confirmar a chegada e permitir o envio do ASO via WhatsApp.',
      );
      return;
    }

    if (exigeBiometria && !biometriaFoto) {
      setError(
        'A biometria facial do candidato é obrigatória para registrar a entrada na recepção desta clínica. Capture a foto ou anexe o arquivo.',
      );
      return;
    }

    if (dataNoFuturo && !motivoAjuste.trim()) {
      setError('Por favor, informe o motivo da antecipação do agendamento.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await clinicaService.darEntradaRecepcao(
        item.agendamento_id,
        clinicaId,
        {
          telefone,
          biometria_foto: biometriaFoto,
          biometria_tipo: biometriaTipo,
          antecipar_para_hoje: dataNoFuturo ? true : undefined,
          motivo_antecipacao: dataNoFuturo
            ? motivoAjuste.trim() || 'Comparecimento antecipado do colaborador'
            : undefined,
        },
      );
      onSuccess(res);
      handleClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Falha ao registrar entrada do paciente na recepção.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAjustarData = async () => {
    if (!item) return;
    if (!novaData || !motivoAjuste.trim()) {
      setError('Preencha a nova data e o motivo para prosseguir.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await clinicaService.ajustarDataAgendamento(item.agendamento_id, {
        data: novaData,
        motivo: motivoAjuste,
      });
      setDataAgendamentoAtual(novaData);
      setIsAjustandoData(false);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao ajustar data do agendamento.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
      <div className="bg-white rounded-3xl max-w-lg w-full border border-gray-200 shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-fade-in">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
              <ClipboardCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                Confirmar Chegada do Colaborador
              </h2>
              <p className="text-xs text-gray-500">
                {exigeBiometria
                  ? 'Confirmação de dados para WhatsApp e biometria facial'
                  : 'Confirmação de dados para fila de atendimento e WhatsApp'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Card Resumo do Candidato */}
          <div className="p-4 rounded-2xl bg-gray-50 border border-gray-200/80 space-y-2">
            <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
              <User className="w-4 h-4 text-primary-600 shrink-0" />
              <span>{item.colaborador_nome}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-gray-500">CPF:</span>
                <span className="font-mono">{item.colaborador_cpf}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span>{item.hora_agendamento || 'Ordem de Chegada'}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:col-span-2">
                <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="truncate">{item.empresa_nome}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:col-span-2">
                <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="truncate font-medium text-primary-700">{item.tipo_exame}</span>
              </div>
            </div>
          </div>

          {/* Alerta de Antecipação de Agendamento Futuro */}
          {dataNoFuturo && (
            <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-950 space-y-2.5">
              <div className="flex items-start gap-2.5">
                <div className="p-1.5 rounded-xl bg-amber-100 text-amber-700 shrink-0 mt-0.5">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-amber-900">
                      Comparecimento Antecipado
                    </span>
                    <span className="text-xs font-mono font-bold bg-amber-200/80 px-2 py-0.5 rounded text-amber-900">
                      Original: {new Date(dataAgendamentoAtual + 'T12:00:00').toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <p className="text-xs text-amber-900 leading-relaxed">
                    O colaborador está comparecendo antes da data agendada. Ao clicar em <strong>&quot;Confirmar e Antecipar para Hoje&quot;</strong>, este agendamento e seus exames vinculados serão automaticamente antecipados para a data de hoje e disponibilizados imediatamente na fila médica.
                  </p>
                </div>
              </div>

              {/* Campo para motivo da antecipação */}
              <div className="pt-2 border-t border-amber-200/60 space-y-1">
                <label htmlFor="recepcao-motivo-antecipacao" className="block text-xs font-semibold text-amber-950">
                  Motivo da Antecipação: <span className="text-rose-500 font-bold">*</span>
                </label>
                <input
                  id="recepcao-motivo-antecipacao"
                  type="text"
                  value={motivoAjuste}
                  onChange={(e) => setMotivoAjuste(e.target.value)}
                  placeholder="Ex: Comparecimento antecipado do colaborador"
                  className="w-full px-3 py-2 bg-white border border-amber-300 rounded-xl text-xs text-gray-900 font-medium focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>
            </div>
          )}

          {/* Ajustar Data do Agendamento */}
          <div className="p-4 rounded-2xl bg-white border border-gray-200 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-gray-800 block">Data do Agendamento</span>
                <span className="text-xs text-gray-600 font-medium">
                  {new Date(dataAgendamentoAtual + 'T12:00:00').toLocaleDateString('pt-BR')}
                </span>
              </div>
              {!isAjustandoData && (
                <button
                  type="button"
                  onClick={() => setIsAjustandoData(true)}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 hover:underline cursor-pointer"
                >
                  Reagendar para outra data
                </button>
              )}
            </div>
            
            {isAjustandoData && (
              <div className="space-y-3 pt-2 border-t border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Nova Data</label>
                    <input
                      type="date"
                      value={novaData}
                      onChange={(e) => setNovaData(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Motivo do Reagendamento</label>
                    <input
                      type="text"
                      value={motivoAjuste}
                      onChange={(e) => setMotivoAjuste(e.target.value)}
                      placeholder="Ex: Solicitação da empresa parceira"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleAjustarData}
                    disabled={submitting || !novaData || !motivoAjuste.trim()}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? 'Salvando...' : 'Confirmar Troca de Data'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsAjustandoData(false)}
                    disabled={submitting}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold cursor-pointer"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Erro Geral */}
          {error && (
            <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Confirmação do Celular para Envio do ASO via WhatsApp */}
          <div className="p-4 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 via-white to-emerald-50/20 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-xl bg-emerald-100 text-emerald-700">
                  <MessageCircle className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                      Celular do Colaborador (WhatsApp)
                    </span>
                    <span className="text-rose-500 font-bold">*</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-200 uppercase">
                      Envio do ASO
                    </span>
                  </div>
                  <p className="text-2xs text-gray-500">
                    Obrigatório para entrar na fila de atendimento e receber a 2ª via assinada do ASO
                  </p>
                </div>
              </div>
            </div>

            {/* Roteiro Amigável para o Operador da Recepção */}
            <div className="p-3 rounded-xl bg-emerald-100/70 border border-emerald-200 text-emerald-950 space-y-1">
              <div className="text-2xs font-bold uppercase tracking-wider text-emerald-900 flex items-center gap-1">
                <span>💬 Roteiro de Atendimento (Como solicitar de forma amigável):</span>
              </div>
              <p className="text-xs text-emerald-900 italic font-medium leading-relaxed pl-1.5 border-l-2 border-emerald-400">
                {item.colaborador_telefone
                  ? `"${item.colaborador_nome.split(' ')[0]}, para que você receba a sua via do ASO diretamente no seu WhatsApp assim que o médico concluir a consulta, por favor confirme se o seu número continua sendo ${formatPhone(item.colaborador_telefone)} ou se deseja atualizar."`
                  : `"${item.colaborador_nome.split(' ')[0]}, por gentileza, confirme seu número de WhatsApp com DDD para que possamos enviar automaticamente a sua via do ASO assim que o médico concluir o atendimento."`}
              </p>
            </div>

            {/* Campo do Celular com Máscara */}
            <div className="space-y-1">
              <label htmlFor="recepcao-telefone-input" className="block text-xs font-semibold text-gray-700">
                Número do Celular (com DDD):
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 z-10">
                  <Phone className="w-4 h-4 text-emerald-600" />
                </div>
                <FormattedInput
                  id="recepcao-telefone-input"
                  type="phone"
                  value={telefone}
                  onChange={(masked) => setTelefone(masked)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all placeholder:text-gray-400 bg-white"
                />
              </div>
              <span className="text-[11px] text-gray-500 block">
                O telefone será atualizado no cadastro do colaborador caso seja alterado.
              </span>
            </div>
          </div>

          {/* Seção da Biometria Facial */}
          {exigeBiometria && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                  Foto Facial do Colaborador
                </span>
                <span className="text-rose-500 font-bold">*</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-100 text-rose-800 border border-rose-200 uppercase">
                  Obrigatória
                </span>
              </div>
              {biometriaFoto && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary-100 text-primary-800">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary-600" />
                  Foto Pronta
                </span>
              )}
            </div>

            {cameraError && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>{cameraError}</span>
              </div>
            )}

            {/* Captura / Câmera / Upload */}
            {!biometriaFoto ? (
              <div className="space-y-3">
                {isCameraActive ? (
                  <div className="relative rounded-2xl overflow-hidden bg-slate-950 aspect-[4/3] max-w-sm mx-auto flex items-center justify-center border-2 border-primary-500 shadow-lg">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover scale-x-[-1]"
                    />

                    {/* Guia Oval Pontilhado */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <div className="w-44 h-56 rounded-[50%] border-2 border-dashed border-primary-400 bg-primary-500/10 shadow-[0_0_0_9999px_rgba(15,23,42,0.65)] flex flex-col items-center justify-between py-4">
                        <span className="text-[10px] font-bold text-primary-200 bg-slate-900/80 px-2 py-0.5 rounded-full">
                          Topo da Cabeça
                        </span>
                        <span className="text-[10px] font-bold text-primary-200 bg-slate-900/80 px-2 py-0.5 rounded-full">
                          Queixo aqui
                        </span>
                      </div>
                      <p className="mt-2 text-[11px] font-semibold text-white bg-slate-900/90 px-2.5 py-0.5 rounded-full border border-slate-700">
                        Enquadre o rosto no contorno
                      </p>
                    </div>

                    {/* Controles da Câmera */}
                    <div className="absolute bottom-3 inset-x-0 flex items-center justify-center gap-2 px-3 z-10">
                      <button
                        type="button"
                        onClick={captureSnapshot}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 active:scale-95 text-white font-bold text-xs rounded-xl shadow-md flex items-center gap-1.5 cursor-pointer transition-all"
                      >
                        <Camera className="w-4 h-4" />
                        <span>Capturar Foto</span>
                      </button>
                      <button
                        type="button"
                        onClick={stopCamera}
                        className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs rounded-xl shadow-md cursor-pointer transition-all"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={startCamera}
                      className="p-4 rounded-2xl border-2 border-dashed border-primary-300 hover:border-primary-500 bg-primary-50/50 hover:bg-primary-50 transition-all flex flex-col items-center justify-center text-center gap-2 cursor-pointer group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-primary-100 group-hover:bg-primary-200 text-primary-600 flex items-center justify-center transition-colors">
                        <Camera className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-primary-900 block">
                          Tirar Foto pela Webcam
                        </span>
                        <span className="text-[11px] text-primary-600 mt-0.5 block">
                          Abre a câmera frontal com guia de enquadramento
                        </span>
                      </div>
                    </button>

                    <label className="p-4 rounded-2xl border-2 border-dashed border-slate-300 hover:border-primary-400 bg-slate-50/50 hover:bg-primary-50/30 transition-all flex flex-col items-center justify-center text-center gap-2 cursor-pointer group">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-primary-100 text-slate-600 group-hover:text-primary-600 flex items-center justify-center transition-colors">
                        <Upload className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-slate-800 group-hover:text-primary-900 block">
                          Anexar Foto Salva
                        </span>
                        <span className="text-[11px] text-slate-500 group-hover:text-primary-700 mt-0.5 block">
                          Carregar arquivo de foto (.jpg, .jpeg, .png)
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                    </label>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center gap-3.5 p-3.5 rounded-2xl border border-primary-200 bg-primary-50/60">
                <div className="relative w-24 h-28 rounded-xl overflow-hidden border-2 border-primary-500 shadow-xs shrink-0 bg-slate-900">
                  <img
                    src={biometriaFoto}
                    alt="Biometria Facial do Colaborador"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-primary-700/90 text-white text-[9px] font-bold py-0.5 text-center">
                    {biometriaTipo === 'WEBCAM' ? 'Captura Câmera' : 'Arquivo Anexo'}
                  </div>
                </div>
                <div className="flex-1 space-y-1 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-1.5 text-primary-900 font-bold text-xs">
                    <CheckCircle2 className="w-4 h-4 text-primary-600 shrink-0" />
                    <span>Foto Facial Coletada com Sucesso</span>
                  </div>
                  <p className="text-[11px] text-primary-700 leading-relaxed">
                    A foto comprova a presença presencial do candidato e será aproveitada na ficha médica do atendimento.
                  </p>
                  <div className="pt-1 flex items-center justify-center sm:justify-start">
                    <button
                      type="button"
                      onClick={() => setBiometriaFoto(null)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer font-semibold"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Substituir / Tirar Outra</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 space-y-3">
          {/* Pendências se o botão estiver bloqueado */}
          {!canConfirm && (
            <div className="p-2.5 rounded-xl bg-slate-100/90 border border-slate-200 text-2xs text-slate-700 space-y-1">
              <span className="font-bold text-slate-800 uppercase tracking-wider block">
                Pendências para liberar entrada:
              </span>
              <div className="flex flex-wrap gap-2 text-xs">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${
                    isTelefoneValido
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold'
                      : 'bg-rose-50 text-rose-700 border border-rose-200 font-semibold'
                  }`}
                >
                  {isTelefoneValido ? <CheckCircle2 className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                  Celular com DDD ({rawTel.length}/10 ou 11 dígitos)
                </span>
                {exigeBiometria && (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${
                      isBiometriaValida
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold'
                        : 'bg-rose-50 text-rose-700 border border-rose-200 font-semibold'
                    }`}
                  >
                    {isBiometriaValida ? <CheckCircle2 className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    Foto Facial
                  </span>
                )}
                {dataNoFuturo && !isMotivoValido && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 font-semibold">
                    <X className="w-3.5 h-3.5" />
                    Motivo da Antecipação
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-bold transition-all cursor-pointer"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={handleConfirmar}
              disabled={!canConfirm}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-xs font-bold shadow-xs hover:shadow-md transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Registrando Entrada...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{dataNoFuturo ? 'Confirmar e Antecipar para Hoje' : 'Confirmar Dados e Liberar'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecepcaoCheckinModal;
